import type { IExtraService } from '@/types';
import type { ComponentWithAs, IconProps } from '@chakra-ui/react';
import { Box, Button, Flex, Text, Badge } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

const commonRemoveBtnStyles = {
  color: 'brand.red.light',
  fontWeight: 'normal',
  px: 0,
  variant: 'link',
};

const renderRemoveButton = (
  label: string,
  render: boolean,
  styles: Record<string, unknown>,
  onClick: () => void
): JSX.Element | null =>
  render ? (
    <Button {...commonRemoveBtnStyles} {...styles} onClick={onClick}>
      {label}
    </Button>
  ) : // eslint-disable-next-line unicorn/no-null
  null;

const renderAdditionalProperties = (
  additionalProperties: IExtraServiceRowProps['additionalProperties'],
  badgeTexts: { isWoodenBox: string }
): JSX.Element => (
  <>
    {additionalProperties && (
      <Text>{additionalProperties.isWoodenBox && <Badge>{badgeTexts.isWoodenBox}</Badge>}</Text>
    )}
  </>
);

interface IExtraServiceRowProps extends IExtraService {
  description: string;
  icon: ComponentWithAs<'svg', IconProps>;
  id: string;
  isShipmentDoc?: boolean;
  isVisible?: boolean;
  name: string;
  onClick: () => void;
}

export const ExtraServiceRow: React.FC<IExtraServiceRowProps> = ({
  additionalProperties,
  description,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  icon: Icon,
  id,
  isRemovable,
  isShipmentDoc,
  isVisible,
  name,
  onClick,
}): JSX.Element => {
  const { t } = useTranslation();

  if (!isVisible) {
    return (
      <Flex borderBottom="1px" borderColor="black">
        {''}
      </Flex>
    );
  }

  const desktopBtn = renderRemoveButton(
    t('ACTIONS.REMOVE'),
    isRemovable,
    { display: { base: 'none', md: 'block' } },
    onClick
  );

  const mobileBtn = renderRemoveButton(
    t('ACTIONS.REMOVE'),
    isRemovable,
    { alignSelf: 'flex-start', display: { base: 'block', md: 'none' } },
    onClick
  );

  const additionalPropertiesComponent = renderAdditionalProperties(additionalProperties, {
    isWoodenBox: t('EXTRA_SERVICES.PACKAGING_WOODEN_BOX_PROPERTY'),
  });

  return (
    <Flex
      _last={{ borderBottom: '1px', borderColor: 'black' }}
      alignItems="center"
      borderColor="black"
      borderTop="1px"
      flexDirection={{ base: 'column', md: 'row' }}
      gap={{ base: 4, md: 8 }}
      justifyContent="space-between"
      key={id}
      pl={8}
      py={{ base: 4, md: 12 }}
    >
      <Flex alignItems="center" flexBasis={{ base: 'max-content', md: '800px' }} gap={8} w="full">
        <Icon h={20} w={20} />
        <Box>
          <Text fontSize={{ base: 'h3.base', md: 'h3.md' }} fontWeight="bold">
            {t(name)}
          </Text>
          {additionalPropertiesComponent}
          {isShipmentDoc ? '' : desktopBtn}
        </Box>
      </Flex>
      <Text flexGrow={1} maxWidth={{ base: '100%', md: '50%' }} minW="50%">
        {t(description)}
      </Text>

      {isShipmentDoc ? '' : mobileBtn}
    </Flex>
  );
};
